/**
 * URI display utilities for friendly fallback labels.
 */

/**
 * Extract the local name from a URI using the last '#' or '/'.
 * Returns null if no usable local name can be derived.
 */
export function extractUriLocalName(uri: string): string | null {
    if (!uri) return null;

    const hashIdx = uri.lastIndexOf('#');
    const slashIdx = uri.lastIndexOf('/');
    const splitIdx = Math.max(hashIdx, slashIdx);

    // Only humanize URI local names when a URI delimiter is present.
    if (splitIdx < 0) return null;

    const local = uri.slice(splitIdx + 1);
    if (!local) return null;

    try {
        return decodeURIComponent(local);
    } catch {
        return local;
    }
}

/**
 * Convert an identifier to a human-readable sentence-case label.
 *
 * Examples:
 * - ownsTrack -> Owns track
 * - hasDirtyLinearCoordinateBegin -> Dirty linear coordinate begin
 * - InfrastructureElement -> Infrastructure element
 * - has_spot-location -> Spot location
 */
export function humanizeIdentifier(identifier: string): string | null {
    if (!identifier) return null;

    const withWordBoundaries = identifier
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (!withWordBoundaries) return null;

    const withoutLeadingHas = withWordBoundaries.replace(/^has\s+/i, '');
    if (!withoutLeadingHas) return null;

    const lower = withoutLeadingHas.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Humanize the local name segment of a URI for fallback display.
 */
export function humanizeUriLocalName(uri: string): string | null {
    const localName = extractUriLocalName(uri);
    if (!localName) return null;
    return humanizeIdentifier(localName);
}
